import type { Ref } from "react";
import { Alert, AsyncSection, Button, Switch } from "@playbookai/ui";
import { usePersonalPreferences } from "./usePersonalPreferences.js";
export function PersonalPanePreferences({
  group,
  vsmGroup = false,
  firstControlRef,
}: {
  group: boolean;
  vsmGroup?: boolean;
  firstControlRef: Ref<HTMLInputElement>;
}) {
  const state = usePersonalPreferences();
  /* The read's wait and its failure through AsyncSection. The failure is
     the one-line error row with its Retry (OK-31); the wait keeps today's
     plain line, since a Spinner beside it is a look change no PR 3 row
     approves. */
  if (!state.query.data)
    return (
      <AsyncSection
        status={state.query.isError ? "error" : "loading"}
        size="sm"
        loadingLabel="preferences"
        errorTitle="Could not load preferences."
        onRetry={() => {
          if (!state.query.isFetching) void state.query.refetch();
        }}
        skeleton={<p>Loading preferences…</p>}
      >
        {null}
      </AsyncSection>
    );
  return (
    <div className="space-y-3">
      {/* Kept hand-built: SettingRow compact adds a divider and py-2, a look
          change no PR 3 row approves. */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium">
            {vsmGroup ? "Your auto approval" : "Auto approval"}
          </p>
          <p className="text-dense-2 text-pb-text-muted">
            Required approvals still apply.
          </p>
        </div>
        <Switch
          ref={firstControlRef}
          aria-label="Auto approval"
          disabled={state.pending}
          checked={
            state.failed?.auto_approve_internal_tools ??
            state.query.data.auto_approve_internal_tools
          }
          onChange={(enabled) =>
            void state.save({ auto_approve_internal_tools: enabled })
          }
        />
      </div>
      {vsmGroup ? (
        <p className="text-xs text-pb-text-muted">
          Group auto approval follows the facilitator’s setting, or the owner’s
          when no facilitator is assigned. This switch changes only your
          personal setting.
        </p>
      ) : group ? (
        <p className="text-xs text-pb-text-muted">
          SOLVE follows the facilitator’s tone, or the owner’s when there is no
          facilitator.
        </p>
      ) : null}
      {/* Kept: SaveStatus is an inline-flex line on the parent's strut
          and paints a failed save red, a look change no PR 3 row approves. */}
      <p role="status" className="text-caption text-pb-text-muted">
        {state.pending
          ? "Saving…"
          : state.error
            ? "Changes not saved"
            : "All changes saved"}
      </p>
      {state.error && (
        <Alert tone="critical">
          {state.error}
          <Button size="sm" onClick={state.retry}>
            Retry
          </Button>
        </Alert>
      )}
    </div>
  );
}
